import styled from "styled-components";

import { TabularRasaPagination } from "./TabularRasaPagination";
import { getTheme, themed, } from "@bracket/ui/themes";

import * as ACCESSORS from "./accessors";
import { Flow, Slate, } from "@bracket/ui/nodes";
import { RasaBody, } from "./body";
import { RasaHeaderRow, } from "./header";
import { getTabularRasaStore, type RasaColumnOptions, } from "./RasaStore";

import type { SizeVariant, } from "@bracket/ui/types";
import type { TabularRasaAccessorFn,  } from "./types";




export declare type TabularRasaProps<T extends Record<string, any>> = {
	id: string;
	accessors?: Partial<Record<keyof T, keyof typeof ACCESSORS | TabularRasaAccessorFn<keyof T, T>>>;
	accessorData?: Record<string, any>;
	bodyRowMargin?: string;
	bodyRowPadding?: string;
	cellMargin?: string;
	cellPadding?: string;
	columns?: Partial<Record<keyof T, RasaColumnOptions<T>>>;
	data: T[];
	finalPass?: (data: T[]) => T[];
	headerRowMargin?: string;
	headerRowPadding?: string;
	hide?: string;
	fillPageWithEmptyRows?: boolean;
	paginate?: boolean;
	pageSize?: number;
	paginationAlign?: "LEFT" | "CENTER" | "RIGHT";
	rowHeight?: string;
	rowMargin?: string;
	rowMinHeight?: string;
	rowMinWidth?: string;
	rowPadding?: string;
	rowWidth?: string;
	pk?: keyof T;
	sort?: keyof T | ((a: T, b: T) => number);
	sortDirection?: "asc" | "desc";
	textSize?: SizeVariant;
	onPageSelect?: (page: number) => void;
};


/**
 * TabularRasa renders a paginated, sortable table and a navigation bar beneath.
 * Disables prev on page 1 and next on last page, pads last page with empty rows,
 * and ensures consistent row heights and column widths (including padding rows).
 *
 * @template T - Row data type (must be object)totalItems
 * @param {TabularRasaProps<T>} props
 * @returns {JSX.Element}
 */
export const TabularRasa = <T extends Record<string, any>>(props: TabularRasaProps<T> ) => {
	themed();

	const {
		id,
		data,
		columns,
		paginationAlign,
		paginate = true,
		onPageSelect = () => {},
	} = props;

	if(!id) {
		throw new Error("TabularRasa id is required");
	}

	const { ready, columnKeys, } = getTabularRasaStore<T>(props)();


	return (
		<TabularRasaMain>
			<TRScrollWrapper>
			{ ready && columnKeys.size > 0 && (
					<TRContainer>
						<RasaHeaderRow<T> id={id} />
						<RasaBody<T> id={id} />
					</TRContainer>
				)
			}
			</TRScrollWrapper>
			{ paginate && <TabularRasaPagination id={id} onSelect={onPageSelect} align={paginationAlign} /> }
		</TabularRasaMain>
	);

};

const TabularRasaMain = styled(Slate)`
	background: transparent!important;
	font-family: ${() => getTheme().fonts.body};
	padding: 1rem;
`;

const TRScrollWrapper = styled(Flow)`
	overflow: auto;
	&::-webkit-scrollbar {
		background-color: ${() => getTheme().surface_container_lowest};
		height: 12px;
	}
	&::-webkit-scrollbar-thumb {
		background-color: ${() => getTheme().surface_on};
		border-radius: 8px;
		cursor: pointer;
		&:hover {
			background-color: ${() => getTheme().primary_on};
		}
	}
	&::-webkit-scrollbar-track {
		background-color: ${() => getTheme().surface_container_lowest};
	}
`;

const TRContainer = styled(Flow)``;
